import {
  aggregateMetricsAsPool,
  calculateMetrics,
  type CalculatedOutputMetrics,
  POOL_SUM_FIELDS,
  POOL_SUM_OR_NULL_FIELDS,
} from '@foresightdata/metrics-calculator';
import * as Sentry from '@sentry/react';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { ColumnMeta } from '../../../types';
import { getEnvironment } from '../../../util/environment';
import { ILegalStructureNodeRef } from '../../../services/queries/MaggieLegalStructureQueries';
import {
  IPortfolioTreeBuild,
  PortfolioTreeNodeKind,
  PortfolioTreeSource,
  weaker,
} from './portfolioTree.types';

export interface IPortfolioTreeRow extends Partial<IMetricsDataModel> {
  treeKey: string;
  treePath: string[];
  /** `treeKey` is path-unique, so several rows share one `treeNodeRef`; a consumer keyed by
   * entity (the structure chart) groups on it. */
  treeNodeRef: ILegalStructureNodeRef;
  treeKind: PortfolioTreeNodeKind;
  treeLabel: string;
  treeSource: PortfolioTreeSource;
  treeIncomplete: boolean;
  /** Rows the node's pool aggregated: ledger leaves plus one synthetic per pooled register value. */
  treeRowCount: number;
  /** The ledger leaves alone — 0 for a node whose whole value came from the register. */
  treeLedgerRowCount: number;
}

export interface ITreeRowContext {
  fundsById: Map<number, IFundDataModel>;
  companyMap: Map<number, ICompanyDataModel>;
  investmentMap: Map<number, IFinanceRoundDataModel>;
  customFundFields: ColumnMeta[];
  asOfDate: Date;
  entityValues: Map<string, { cost: number | null; fmv: number | null; source: string }>;
}

/** Every pooled money field is stated, `null` where the register is silent: a missing field would
 * let the column aggFunc sum the children instead. */
function registerRowFields(cost: number | null, fmv: number | null): Partial<IMetricsDataModel> {
  const fields: Record<string, number | null> = {};
  for (const field of [...POOL_SUM_FIELDS, ...POOL_SUM_OR_NULL_FIELDS]) {
    fields[field] = null;
  }
  return {
    ...fields,
    amountInvested: cost,
    totalCost: cost,
    unrealizedCost: cost,
    fmv,
    unrealizedValue: fmv,
    totalValue: fmv,
    unrealizedGL: cost != null && fmv != null ? fmv - cost : null,
  } as Partial<IMetricsDataModel>;
}

function registerPoolInput(cost: number | null, fmv: number | null): CalculatedOutputMetrics {
  const totalCost = cost ?? 0;
  const value = fmv ?? 0;
  return {
    amountInvested: totalCost,
    totalCost,
    unrealizedCost: totalCost,
    fmv: value,
    unrealizedValue: value,
    totalValue: value,
    unrealizedGL: value - totalCost,
    distributions: 0,
    realizedValue: 0,
    realizedCost: 0,
    realizedGL: 0,
    escrowAmount: 0,
    transactions: [],
    investmentAge: 0,
  } as unknown as CalculatedOutputMetrics;
}

export function computeTreeRows(build: IPortfolioTreeBuild, ctx: ITreeRowContext): IPortfolioTreeRow[] {
  const leafInputs = build.leaves.map((leaf) => ({ ...leaf.slice, treeKey: leaf.node.key }));
  const calculated = (() => {
    try {
      return calculateMetrics(
        leafInputs,
        ctx.fundsById,
        ctx.companyMap,
        ctx.asOfDate,
        true,
        ctx.customFundFields,
        getEnvironment() === 'greycroft',
        ctx.investmentMap
      ).filter((row) => row != null);
    } catch (error) {
      // A calculator failure must not crash the tree inside a render-time useMemo.
      Sentry.captureException(error);
      return [];
    }
  })();
  const leafByKey = new Map(calculated.map((row) => [row.treeKey, row]));

  // `treeKey` rides along on the calculator's input rows and must come back out on its output; a
  // version that stops copying unknown fields through degrades the whole tree to register fallbacks.
  if (import.meta.env.DEV && calculated.length > 0 && leafByKey.size === 0) {
    console.warn('computeTreeRows: calculateMetrics returned rows without treeKey; tree will be empty');
  }

  const nodeByKey = new Map(build.nodes.map((node) => [node.key, node]));
  const entityValueOf = (nodeRef: ILegalStructureNodeRef) =>
    ctx.entityValues.get(`${nodeRef.nodeType}-${nodeRef.nodeId}`);

  const rows: IPortfolioTreeRow[] = [];
  for (const node of build.nodes) {
    const meta = {
      treeKey: node.key,
      treePath: node.path,
      treeNodeRef: node.nodeRef,
      treeKind: node.kind,
      treeLabel: node.label,
      treeSource: node.source,
      treeIncomplete: node.incomplete,
    };
    if (node.kind === 'company') {
      const leaf = leafByKey.get(node.key);
      if (leaf) {
        rows.push({
          ...(leaf as unknown as IMetricsDataModel),
          ...meta,
          treeRowCount: 1,
          treeLedgerRowCount: 1,
        });
      }
      continue;
    }
    const leafRows = (build.leafKeysByNode.get(node.key) ?? [])
      .map((k) => leafByKey.get(k))
      .filter((r) => r != null);
    // The register is keyed by entity, so a vehicle's several path-unique nodes read the same row.
    const entity = entityValueOf(node.nodeRef);
    // A vehicle booked at entity level has no leaf of its own; its register value joins its parents'.
    const pooledVehicles = (build.leaflessVehiclesByNode.get(node.key) ?? [])
      .map((key) => nodeByKey.get(key))
      .filter((vehicle) => vehicle != null)
      .map((vehicle) => entityValueOf(vehicle.nodeRef));
    const booked = pooledVehicles.filter((value) => value != null);
    const treeSource = booked.reduce(
      (weakest, value) => weaker(weakest, (value.source as PortfolioTreeSource) ?? 'estimated'),
      leafRows.length > 0 ? node.source : ((entity?.source as PortfolioTreeSource | undefined) ?? node.source)
    );
    // A vehicle the register says nothing about contributes nothing, but the node has to say so.
    const treeIncomplete =
      node.incomplete ||
      pooledVehicles.some((value) => value == null || value.cost == null || value.fmv == null);
    const synthetic = booked.map((value) => registerPoolInput(value.cost, value.fmv));
    const pool = aggregateMetricsAsPool([...leafRows, ...synthetic], ctx.asOfDate);
    if (pool) {
      // A register value has no cashflows, age or company: leave those measures to the leaves alone.
      const fromLedger = synthetic.length > 0 ? aggregateMetricsAsPool(leafRows, ctx.asOfDate) : undefined;
      rows.push({
        ...(pool as unknown as IMetricsDataModel),
        ...(synthetic.length > 0
          ? {
              irr: fromLedger?.irr ?? null,
              investmentAge: fromLedger?.investmentAge ?? 0,
              ownerShipPercentage: fromLedger?.ownerShipPercentage ?? undefined,
            }
          : {}),
        ...meta,
        treeSource,
        treeIncomplete,
        treeRowCount: leafRows.length + synthetic.length,
        treeLedgerRowCount: leafRows.length,
      } as IPortfolioTreeRow);
      continue;
    }
    rows.push({
      ...meta,
      treeSource,
      // Blank has to read as "not booked" rather than "holds nothing".
      treeIncomplete:
        treeIncomplete ||
        (node.kind === 'vehicle' && (entity == null || entity.cost == null || entity.fmv == null)),
      treeRowCount: 0,
      treeLedgerRowCount: 0,
      ...registerRowFields(entity?.cost ?? null, entity?.fmv ?? null),
      moic: undefined,
      irr: null,
      dpi: undefined,
      tvpi: undefined,
    } as IPortfolioTreeRow);
  }
  return rows;
}
